<template>
  <BaseModal ref="modal">
    <UiModalDialog
      class="search-image-file-modal"
      dialog-height="20rem"
      dialog-min-width="20rem"
    >
      <template #header>
        {{ ls({ ja: '画像ファイルの選択', en: 'Select Image File' }) }}
      </template>
      <div class="jps-modal-content upload-modal-content">
        <div class="select-image">
          <BaseUpload
            accept="image/*"
            @update:model-value="selectFile($event)"
            image
            :message="{
              ja: '画像ファイルをドラッグ＆ドロップするか、クリックしてファイルを選択して下さい',
              en: 'Drop your files here or click to select',
            }"
          />
          <p class="vald is-size-8">
            powered by
            <a href="https://github.com/vdaas/vald" target="_blank" rel="noopener">vald</a>
          </p>
        </div>
      </div>
      <template #buttons>
        <button @click="modal.reject()">{{ ls(dapLangGlobal.cancel) }}</button>
        <button @click="analyze()" :disabled="!file">OK</button>
      </template>
      <template #modal-overlay>
        <div
          class="layout-col generated-26"
          v-show="flagAnalyzing"
        >
          <p>
            {{
              lss(
                '画像の特徴を抽出しています',
                'Extracting features of the image',
              )
            }}
          </p>
          <BaseIcon
            :name="isHina ? 'hina/logo-loading' : 'jps/logo-sq-4c'"
            class="generated-39"
          />
        </div>
      </template>
    </UiModalDialog>
  </BaseModal>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'
import type { BaseModal } from '#components'
import { IMF_FEATURE_CACHE } from '~/components/search/search-field/useBaseSearchField'

const { isHina } = useServiceType()
const { ls, lss } = useI18n()
const { snackbarError } = useSnackbar()
const { putImageFeature } = useItem()

const modal = ref<InstanceType<typeof BaseModal>>()
const queryImage: Ref<string> = ref(null)
const file: Ref<File> = ref(null)
const flagAnalyzing: Ref<boolean> = ref(false)
const openAwait = async () => {
  queryImage.value = null
  file.value = null
  flagAnalyzing.value = false
  return modal.value.openAwait<{ id: string; image: string }>()
}
defineExpose({
  openAwait,
})

const MODEL_IMAGE_SIZE = 224
const IMAGESEARCH_ENDPOINT_URL = '/dl/api/imagefeatures/'

const selectFile = (f: File[]) => {
  file.value = f[0]
}

const createResizedCanvasElement = (originalImg: any) => {
  let canvas = document.createElement('canvas')
  let ctx = canvas.getContext('2d')
  ctx.drawImage(
    originalImg,
    0,
    0,
    originalImg.width,
    originalImg.height,
    0,
    0,
    MODEL_IMAGE_SIZE,
    MODEL_IMAGE_SIZE,
  )
  return canvas.toDataURL('image/jpeg')
}

const analyze = () => {
  flagAnalyzing.value = true
  let reader = new FileReader()
  reader.onload = (e: any) => {
    queryImage.value = e.target.result
    setTimeout(() => {
      let img = new Image()
      img.crossOrigin = 'Anonymous'
      img.src = queryImage.value
      img.onload = async () => {
        let dataurl = createResizedCanvasElement(img)
        const param = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ img_b64: dataurl }),
        }
        setTimeout(async () => {
          try {
            const response = await fetch(IMAGESEARCH_ENDPOINT_URL, param)
            const features = await response.json()
            const imfid = await putImageFeature(features['body'])
            putLsCache(IMF_FEATURE_CACHE, imfid, queryImage.value)
            modal.value.resolve({
              image: queryImage.value,
              id: imfid,
            })
          } catch (e) {
            snackbarError({
              ja: '特徴の登録に失敗しました',
              en: 'Failed to save feature',
            })
            flagAnalyzing.value = false
          }
        }, 100)
      }
    }, 100)
  }
  reader.readAsDataURL(file.value)
}
</script>

<style lang="scss">
.search-image-file-modal {
  position: relative;

  .model-loading {
    .jps-logo-icon {
      margin-left: auto;
      margin-right: auto;
    }
  }
  .jps-upload {
    width: 100%;
  }
  .analyzing-blocker {
    background: rgba(255, 255, 255, 0.8);
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 9999;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    cursor: wait;
    .analyzing-message {
      display: flex;
      flex-direction: column;
      align-items: center;
    }
  }
}
</style>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.generated-26 {
    position: absolute;
    left: 0px;
    top: 0px;
    display: flex;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    background-color: $white;
    background-color: rgb(255 255 255 / 0.8);
}
.generated-39 {
    width: 2rem;
    height: 2rem;
    animation: spin 1s linear infinite;
    transition-duration: 200ms;
}
</style>

